import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, Users } from "lucide-react";
import { api, IMAGES } from "@/lib/data";

const NAME_KEY = "community_name";

const palette = ["#C25934", "#2C4C3B", "#8a6d3b", "#6b4f8a", "#3b6b7a", "#a13b5a"];
const colorFor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

const timeAgo = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

export default function CommunityChat() {
  const [name, setName] = useState(localStorage.getItem(NAME_KEY) || "");
  const [joined, setJoined] = useState(!!localStorage.getItem(NAME_KEY));
  const [draftName, setDraftName] = useState("");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/chat/messages");
      setMessages(r.data);
    } catch (e) {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const join = (e) => {
    e.preventDefault();
    const n = draftName.trim();
    if (!n) return;
    localStorage.setItem(NAME_KEY, n);
    setName(n);
    setJoined(true);
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const r = await api.post("/chat/messages", { author: name, text: t });
      setMessages((m) => [...m, r.data]);
      setText("");
    } catch (e) {} finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-28 md:pt-32 pb-20 min-h-screen" data-testid="chat-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-10">
          <p className="overline text-terracotta inline-flex items-center gap-2 justify-center">
            <Users size={15} /> The Community Room
          </p>
          <h1 className="mt-3 font-heading text-5xl sm:text-6xl tracking-tight text-ink">
            Pull up a <span className="italic text-terracotta">chair</span>
          </h1>
          <p className="mt-4 font-body text-ink-soft max-w-xl mx-auto">
            A warm, open space to share, encourage, and pray for one another. Be kind —
            we're all walking each other home.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl bg-white border border-black/5 shadow-xl overflow-hidden"
          data-testid="chat-window"
        >
          <div
            className="px-6 py-4 border-b border-black/5 bg-cover bg-center relative"
            style={{ backgroundImage: `url(${IMAGES.community})` }}
          >
            <div className="absolute inset-0 bg-charcoal/70" />
            <div className="relative flex items-center justify-between text-sand">
              <div>
                <h3 className="font-heading text-2xl">Community Chat</h3>
                <p className="font-body text-xs text-sand/70">{messages.length} messages · everyone welcome</p>
              </div>
              {joined && (
                <span className="font-body text-sm bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">
                  You're in as <strong>{name}</strong>
                </span>
              )}
            </div>
          </div>

          <div
            ref={scrollRef}
            className="h-[46vh] min-h-[320px] overflow-y-auto p-6 space-y-4 bg-sand-alt/30"
            data-testid="chat-messages"
          >
            {messages.length === 0 ? (
              <div className="h-full grid place-items-center text-center text-ink-soft font-body">
                <div>
                  <p className="font-heading text-2xl text-ink">It's quiet in here…</p>
                  <p className="mt-1">Be the first to say hello.</p>
                </div>
              </div>
            ) : (
              messages.map((m) => {
                const mine = m.author === name;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                      <div className="flex items-center gap-2 mb-1 px-1">
                        <span
                          className="font-body text-xs font-semibold"
                          style={{ color: colorFor(m.author) }}
                        >
                          {m.author}
                        </span>
                        <span className="font-body text-[11px] text-ink-soft/60">
                          {timeAgo(m.created_at)}
                        </span>
                      </div>
                      <div
                        data-testid="chat-bubble"
                        className={`rounded-2xl px-4 py-2.5 font-body text-[15px] leading-relaxed ${
                          mine
                            ? "bg-terracotta text-white rounded-br-sm"
                            : "bg-white text-ink border border-black/5 rounded-bl-sm"
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="p-4 border-t border-black/5 bg-white">
            {joined ? (
              <form onSubmit={send} className="flex items-center gap-2" data-testid="chat-form">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Share something kind…"
                  data-testid="chat-input"
                  className="flex-1 rounded-full border border-black/10 bg-sand/60 px-5 py-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
                <button
                  type="submit"
                  disabled={sending || !text.trim()}
                  data-testid="chat-send"
                  className="shrink-0 rounded-full bg-terracotta p-3.5 text-white transition-all hover:bg-terracotta-hover disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-forest"
                >
                  {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                </button>
              </form>
            ) : (
              <form onSubmit={join} className="flex items-center gap-2" data-testid="chat-join-form">
                <input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="Enter your name to join…"
                  data-testid="chat-name-input"
                  className="flex-1 rounded-full border border-black/10 bg-sand/60 px-5 py-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
                <button
                  type="submit"
                  data-testid="chat-join-btn"
                  className="shrink-0 rounded-full bg-forest px-6 py-3 text-sand font-medium transition-all hover:bg-forest-hover focus:outline-none focus:ring-2 focus:ring-terracotta"
                >
                  Join
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
